import { Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  DossierHttpService,
  EventSubHttpService,
  InsightService,
} from '@sc/data-access-http-services';
import { Dossier } from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { AuthService } from '@sc/feature-auth-okta';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { Buffer } from 'buffer';
import { EMPTY, from } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RegisterAppB2BService {
  private currentDossier: Dossier;

  private activatedRegisterAppClose = false;
  private readonly isFeatureRegisterActive: boolean = false;

  private readonly unloadListener: (event: any) => any;

  constructor(
    private readonly dossierHttpService: DossierHttpService,
    private readonly authService: AuthService,
    private readonly storage: StorageService,
    private readonly eventSubHttpService: EventSubHttpService,
    private readonly featureFlippingService: FeatureFlippingManagerService,
    private readonly insightService: InsightService,
    private readonly dossierSandboxService: DossierSandboxService,
    private readonly configService: ConfigurationService,
    private readonly translator: TranslatorService,
    private readonly overlayService: AdaptableOverlayService
  ) {
    this.isFeatureRegisterActive =
      this.featureFlippingService.isFeatureActivated('b2b-register-app-close');

    this.activatedRegisterAppClose = this.isFeatureRegisterActive;

    this.unloadListener = (event) => {
      if (!this.activatedRegisterAppClose) {
        return;
      }

      if (this.currentDossier?.id) {
        this.registerAppClose(true, this.currentDossier.id);

        // NOTE - here we must set a double timeout because if the user takes too much time deciding if he wants to stay or not on the popup
        // then the timeout runs out and its callback is generally fired even after choosing the "close tab" option (some promise lifecycles are still run after popup unfreeze)
        setTimeout(() => {
          setTimeout(() => {
            this.registerAppCloseCancelled(this.currentDossier.id).then(
              (error) => {
                if (error.status === 409) {
                  this.registerSessionDown(this.currentDossier.id);
                  this.activatedRegisterAppClose = false;
                }
              }
            );
          }, 500);
        }, 100);
      }

      const returnMessage = this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.appLeaveDefaultPrompt'
      );

      (event || window.event).returnValue = returnMessage;
      return returnMessage;
    };

    this.dossierSandboxService.dossier$
      .pipe(
        filter((dossier) => !!dossier),
        take(1),
        switchMap((dossier) => {
          if (this.isFeatureRegisterActive) {
            return from(this.registerAppCloseCancelled(dossier.id));
          }

          return EMPTY;
        })
      )
      // NOTE - here, http errors are normal since we call this request only to avoid abandonning on refresh
      .subscribe();

    this.dossierSandboxService.dossier$.subscribe(
      (d) => (this.currentDossier = d)
    );
  }

  async registerAppClose(wait: boolean, dossierId: string) {
    const accessToken = this.authService?.getAccessToken();

    return await this.dossierHttpService.registerAppClose(wait, dossierId, {
      accessToken: accessToken,
      requestId: this.insightService.generateRequestId(),
      clientKey: this.eventSubHttpService.getClientKey(),
    });
  }

  async registerAppCloseCancelled(dossierId: string) {
    const accessToken = this.authService?.getAccessToken();

    return this.dossierHttpService.registerAppCloseCancelled(dossierId, {
      accessToken: accessToken,
      requestId: this.insightService.generateRequestId(),
      clientKey: this.eventSubHttpService.getClientKey(),
    });
  }

  async registerSessionDown(dossierId: string) {
    this.dossierSandboxService
      .abandonDossier({
        id: dossierId,
        comment: this.translator.getInstantTranslation(
          'InsurerDeclarationContainer.quitTabAbandonComment'
        ),
      })
      .subscribe();

    this.overlayService.showOverlay({
      type: 'already-abandonned',
      input: null,
    });
  }

  deactivateRegisterAppClose() {
    this.activatedRegisterAppClose = false;
  }

  startUnloadListener() {
    if (this.isFeatureRegisterActive) {
      window.addEventListener('beforeunload', this.unloadListener);
    }
  }

  stopUnloadListener() {
    window.removeEventListener('beforeunload', this.unloadListener);
  }

  navigateToExitUri(dossierForContractManager?: any) {
    let encodedParamToPush = null;
    if (
      this.featureFlippingService.isFeatureActivated(
        'integ-contract-manager-push-dossier'
      ) ||
      this.featureFlippingService.isFeatureActivated(
        'integ-contract-manager-redirect-dossier-by-param'
      )
    ) {
      // use this to push to the redirect_exit_uri throught the encodedDossier param
      encodedParamToPush = Buffer.from(
        JSON.stringify(dossierForContractManager)
      ).toString('base64');
    }

    // Desactivate the close popup behavior
    this.activatedRegisterAppClose = false;
    window.open(
      (this.storage.getItem('redirect-exit-uri') ||
        this.storage.getItem('redirect-uri') ||
        this.configService.getConfig().website) +
        (encodedParamToPush ? '&dossier=' + encodedParamToPush : ''),
      '_self'
    );
  }
}
